import React, { useState, useEffect, useRef } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter, pointerWithin, MeasuringStrategy } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { stopProp } from '../../utils';
import { size, get } from '../../lib/lodash';
import { useScreen } from '../../styles';

// SortableList migrated from react-beautiful-dnd to @dnd-kit (Phase 6: PFM-2599).
// API preserved for consumers (create-scorecard, manage-data, form/index.js):
//   - `items`: [{ key, content (node|fn), canDrag?, stripeBackground?, items? }]
//   - `onSort(nextItems)`
//   - `verticalOnly`, `type` ('grid' forces horizontal)
// Nested `item.items` support from the react-beautiful-dnd implementation is
// preserved (PFM-2634): rows may carry a nested `items` array which is rendered
// recursively. Dragging works both within a nested group and across sibling
// groups (e.g. Room Checkpoints Enabled<->Disabled, Scorecard step 3 checklist
// reordering under a superset). When `item.items` is absent the behavior is a
// plain flat list, identical to a non-nested consumer.

const getListStyle = ({ isMobile, ...props }) => {
  const forceGrid = props.type === 'grid';
  const isRow = forceGrid || (!isMobile && !props.verticalOnly);
  return {
    display: isRow ? 'flex' : 'block',
    flexDirection: isRow ? 'row' : 'column',
    flexWrap: 'wrap',
  };
};

// `dragActive` is true whenever ANY drag is in progress anywhere in the list.
// While dragging we suppress the zebra/tile stripeBackground on every row so the
// alternating colors don't visibly swap as dnd-kit reorders rows (PFM-2634).
const getItemStyle = (transform, transition, isDragging, stripeBackground, dragBackground, dragActive) => {
  const base = {
    userSelect: 'none',
    position: 'relative',
    borderRadius: 5,
    transform: CSS.Transform.toString(transform),
    transition,
  };
  if (isDragging) {
    // The floating DragOverlay clone is what the user sees moving under the
    // cursor (rbd-style). Hide the original in place so it reads as a clean
    // placeholder gap instead of a faint second copy ghosting alongside the
    // overlay — this is what made the drag feel "doubled" vs. the old library.
    base.opacity = 0;
  } else if (stripeBackground && !dragActive) {
    base.background = stripeBackground;
  }
  return base;
};

// Shared drag-handle styles (the grip icon vertically centered on the row's own
// title block, so it stays aligned even when the row has nested children below).
const handleStyle = (grabbing) => ({
  position: 'absolute',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: grabbing ? 'grabbing' : 'grab',
  touchAction: 'none',
});

const renderContent = (item) => (typeof item.content === 'function' ? item.content() : item.content);

const SortableRow = ({ item, frozenKeys, dragActive, strategy, bubbleEmptyClicks }) => {
  const theme = useTheme();
  const canDrag = get(item, 'canDrag', true);
  // A `canDrag:false` row (e.g. the Enabled/Disabled group headers on the Room
  // Order tab) can't be picked up, but it MUST stay a valid drop target so a
  // leaf can be dropped into that group — including when the group is empty and
  // has no leaf for closestCenter to land on. So disable only the draggable,
  // never the droppable (PFM-2634).
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
    disabled: { draggable: !canDrag, droppable: false },
  });
  const nested = item.items;
  const childKeys = nested ? nested.map((c) => c.key) : null;
  // Descendants of the row currently being dragged are "frozen": they must NOT
  // receive the make-room sorting transform, otherwise they shift independently
  // of the dragged parent (which they also move with via DOM nesting) and
  // visually collapse onto it. Frozen rows simply travel with their parent.
  const frozen = !!frozenKeys && frozenKeys.has(item.key);
  const effTransform = frozen ? null : transform;
  const effTransition = frozen ? 'none' : transition;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={getItemStyle(effTransform, effTransition, isDragging, get(item, 'stripeBackground'), theme.palette.action.hover, dragActive)}
      // When `bubbleEmptyClicks` is set (DropArea photo grid, PFM-2680), DON'T
      // swallow clicks on the row's empty/margin space — let them bubble to the
      // dropzone root so the file picker opens. The rendered content is
      // responsible for stopping propagation on its own interactive controls
      // (thumbnail -> full screen, caption, delete). The drag handle below
      // always keeps stopProp regardless.
      onClick={bubbleEmptyClicks ? undefined : stopProp}
    >
      <div style={{ position: 'relative' }}>
        {canDrag && (
          <div {...listeners} onClick={stopProp} style={handleStyle(false)}>
            <Icon>drag_indicator</Icon>
          </div>
        )}
        <div style={{ marginLeft: canDrag ? 26 : undefined }}>{renderContent(item)}</div>
      </div>
      {!!nested && (
        <div style={{ marginLeft: canDrag ? 26 : undefined }} onClick={size(nested) ? stopProp : undefined}>
          {!size(nested) && <div style={{ height: 40 }} />}
          {/* Each nested group gets its OWN SortableContext so dnd-kit only
              applies make-room transforms to rows within the same group. This
              keeps a cross-group drag (e.g. Room Order Enabled<->Disabled) from
              shifting the entire list — only the hovered group makes room — and
              still lets closestCenter resolve the drop into the other group
              (PFM-2634). */}
          <SortableContext items={childKeys} strategy={strategy}>
            {nested.map((child) => (
              <SortableRow key={child.key} item={child} frozenKeys={frozenKeys} dragActive={dragActive} strategy={strategy} bubbleEmptyClicks={bubbleEmptyClicks} />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

// The floating clone shown under the cursor while dragging. It is PURELY
// presentational — it must NOT call useSortable, or it would register a second
// sortable node for the active id and the real overlay would never render (this
// was the "no drag copy / item disappears" bug). It just paints the row's
// content (and any nested children) with a lifted-card look (PFM-2634).
const OverlayRow = ({ item }) => {
  const theme = useTheme();
  const canDrag = get(item, 'canDrag', true);
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 5,
        userSelect: 'none',
        cursor: 'grabbing',
        background: get(item, 'stripeBackground') || theme.palette.background.paper,
        boxShadow: theme.shadows[6],
      }}
    >
      <div style={{ position: 'relative' }}>
        {canDrag && (
          <div style={handleStyle(true)}>
            <Icon>drag_indicator</Icon>
          </div>
        )}
        <div style={{ marginLeft: canDrag ? 26 : undefined }}>{renderContent(item)}</div>
      </div>
      {!!item.items && (
        <div style={{ marginLeft: canDrag ? 26 : undefined }}>
          {item.items.map((child) => (
            <OverlayRow key={child.key} item={child} />
          ))}
        </div>
      )}
    </div>
  );
};

// Structural clone of the items tree that preserves leaf payloads by reference
// (notably `content` render functions, which JSON serialization would drop).
// Only the array/object containers are recreated so React state is never
// mutated in place.
const cloneTree = (list) => (list || []).map((item) => (item.items ? { ...item, items: cloneTree(item.items) } : { ...item }));

// Depth-first list of every draggable key in the tree (parents + nested
// children), in render order. Used to populate SortableContext so cross-group
// and nested drags are recognised by dnd-kit.
const collectKeys = (list) => {
  const keys = [];
  for (const item of list || []) {
    keys.push(item.key);
    if (item.items) keys.push(...collectKeys(item.items));
  }
  return keys;
};

// Locate a key within the (possibly nested) tree. Returns the containing array
// (`parent`), the `index` within it, the matched `item`, and the `depth`
// (0 = top level), or null.
const locate = (list, key, depth = 0) => {
  for (let index = 0; index < (list || []).length; index++) {
    const item = list[index];
    if (item.key === key) return { parent: list, index, item, depth };
    if (item.items) {
      const found = locate(item.items, key, depth + 1);
      if (found) return found;
    }
  }
  return null;
};

// Resolve the destination container + insertion index for a cross-group move,
// given the dragged item's location (`from`) and the drop target's location
// (`overLoc`). Returns null when the levels are incompatible (e.g. nesting a
// superset inside another). Shared by onDragOver (live relocation) and onDragEnd.
const resolveDestination = (from, overLoc) => {
  if (overLoc.depth === from.depth) {
    // Over target is a sibling leaf in another group — insert before it.
    return { destParent: overLoc.parent, destIndex: overLoc.index };
  }
  if (from.depth === overLoc.depth + 1 && overLoc.item.items) {
    // Dragging a leaf onto a shallower container row (e.g. a group header or an
    // empty group) — drop into that container at the end.
    return { destParent: overLoc.item.items, destIndex: overLoc.item.items.length };
  }
  return null;
};

// Multi-container collision detection. closestCenter alone makes a group hard to
// target across lists (the dragged item's center must beat every other center),
// so prefer whatever droppable the POINTER is actually inside; among those, pick
// the closest center so a specific leaf row wins over its enclosing group header
// (whose droppable rect spans the whole group). Falls back to closestCenter when
// the pointer is outside every droppable (PFM-2634).
const collisionDetectionStrategy = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) {
    const ids = new Set(pointerHits.map((c) => c.id));
    return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => ids.has(c.id)) });
  }
  return closestCenter(args);
};

export const SortableList = (props) => {
  const [items, setItems] = useState(props.items);
  const [activeId, setActiveId] = useState(null);
  const { isMobile } = useScreen();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  // Snapshot of the list as it was when the drag started. onDragOver relocates
  // items across groups live, so a cancel or a drop outside any target must roll
  // back to this snapshot rather than leave the list in a half-moved state.
  const preDragItems = useRef(null);

  const onDragStart = (event) => {
    preDragItems.current = items;
    setActiveId(event.active.id);
  };

  const onDragCancel = () => {
    if (preDragItems.current) setItems(preDragItems.current);
    preDragItems.current = null;
    setActiveId(null);
  };

  useEffect(() => {
    setItems(props.items);
  }, [props.items]);

  // While the drag is in progress, relocate the dragged leaf into whatever group
  // the pointer is currently over. This is the canonical @dnd-kit multi-container
  // pattern: only the SortableContext that actually CONTAINS the active item
  // opens a make-room gap, so the active item must join the hovered group mid-drag
  // for that group to register as a drop target (and for the source group to
  // collapse). Without this, dragging Disabled->Enabled showed no gap in Enabled
  // (PFM-2634). Parent state isn't touched until drop (onSort fires in onDragEnd).
  const onDragOver = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const hasNested = prev.some((i) => !!i.items);
      if (!hasNested) return prev;
      const next = cloneTree(prev);
      const from = locate(next, active.id);
      const overLoc = locate(next, over.id);
      if (!from || !overLoc) return prev;
      // Same group: dnd-kit's sorting strategy already opens the gap; no move.
      if (from.parent === overLoc.parent) return prev;
      const dest = resolveDestination(from, overLoc);
      if (!dest || from.parent === dest.destParent) return prev;
      const [moved] = from.parent.splice(from.index, 1);
      dest.destParent.splice(dest.destIndex, 0, moved);
      return next;
    });
  };

  const onDragEnd = (event) => {
    setActiveId(null);
    const snapshot = preDragItems.current;
    preDragItems.current = null;
    const { active, over } = event;

    // Flat (non-nested) fast path — identical to the prior behavior.
    const hasNested = items.some((i) => !!i.items);
    if (!hasNested) {
      if (!over || active.id === over.id) return;
      const oldIndex = items.findIndex((i) => i.key === active.id);
      const newIndex = items.findIndex((i) => i.key === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(items, oldIndex, newIndex);
      setItems(next);
      props.onSort && props.onSort(next);
      return;
    }

    // Nested case: `items` already reflects any cross-group relocation applied
    // live by onDragOver. A drop outside every droppable rolls that back.
    if (!over) {
      if (snapshot && snapshot !== items) setItems(snapshot);
      return;
    }

    const next = cloneTree(items);
    const from = locate(next, active.id);
    const overLoc = locate(next, over.id);
    if (!from || !overLoc) {
      // Defensive: still commit a relocation that happened during the drag.
      if (snapshot !== items) props.onSort && props.onSort(items);
      return;
    }

    if (from.parent === overLoc.parent) {
      // Same container — the common case once onDragOver has moved the item into
      // the hovered group, plus all plain same-group reorders. arrayMove handles
      // BOTH directions (the prior hand-rolled splice cancelled downward moves).
      if (from.index !== overLoc.index) {
        const moved = arrayMove(from.parent, from.index, overLoc.index);
        from.parent.length = 0;
        from.parent.push(...moved);
        setItems(next);
        props.onSort && props.onSort(next);
        return;
      }
      // Dropped on its own slot. Commit only if a relocation actually changed the
      // tree during the drag; otherwise it's a true no-op (avoid a spurious save).
      if (snapshot !== items) props.onSort && props.onSort(items);
      return;
    }

    // Cross-container move where onDragOver never relocated (e.g. a very fast
    // drop). Determine the destination, pull the item out, then insert.
    const dest = resolveDestination(from, overLoc);
    if (!dest) {
      // Incompatible levels (e.g. nesting a superset) — roll back any relocation.
      if (snapshot && snapshot !== items) setItems(snapshot);
      return;
    }
    const [moved] = from.parent.splice(from.index, 1);
    dest.destParent.splice(dest.destIndex, 0, moved);
    setItems(next);
    props.onSort && props.onSort(next);
  };

  const forceGrid = props.type === 'grid';
  const isRow = forceGrid || (!isMobile && !props.verticalOnly);
  const strategy = isRow ? rectSortingStrategy : verticalListSortingStrategy;

  // While a parent row is being dragged, freeze its descendants so they ride
  // along with it rather than receiving their own sorting transforms (which
  // would collapse them onto the dragged parent).
  const activeLoc = activeId ? locate(items, activeId) : null;
  const frozenKeys = activeLoc && activeLoc.item.items ? new Set(collectKeys(activeLoc.item.items)) : null;
  const dragActive = !!activeId;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      // Re-measure droppables continuously. Because onDragOver relocates rows
      // across groups mid-drag, cached (drag-start) rects go stale and the gaps
      // snap; measuring Always keeps them fresh so neighbors slide smoothly into
      // place like the old react-beautiful-dnd animation (PFM-2634).
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      {/* Top-level SortableContext holds only the top-level keys; each nested
          group registers its own context (see SortableRow). Scoping transforms
          per group is what keeps a cross-group drag from moving the whole list
          (PFM-2634). All sortables still share this one DndContext, so
          closestCenter can resolve a drop into any group/depth. */}
      <SortableContext items={items.map((i) => i.key)} strategy={strategy}>
        {/* `bubbleEmptyClicks` (DropArea photo grid, PFM-2680): let clicks on the
            list's empty/whitespace area pass through to the parent (the dropzone
            root) so the file picker opens. Otherwise keep the historical
            behavior of stopping propagation so row clicks don't reach a parent
            handler (create-scorecard, manage-data, room order, etc.). */}
        <div style={getListStyle({ ...props, isMobile })} onClick={!props.bubbleEmptyClicks && size(items) ? stopProp : undefined}>
          {items.map((item) => (
            <SortableRow key={item.key} item={item} frozenKeys={frozenKeys} dragActive={dragActive} strategy={strategy} bubbleEmptyClicks={props.bubbleEmptyClicks} />
          ))}
        </div>
      </SortableContext>
      {/* Floating clone of the dragged row under the cursor. This makes the
          in-list rows only show make-room gaps and stops the "whole list comes
          with the item" cross-group/bottom->top glitch (PFM-2634). */}
      {/* zIndex must clear the MUI modal layer (1300): this list renders inside
          the full-screen Room Assignments modal, and the overlay is portaled to
          document.body — without a higher zIndex the floating clone renders
          BEHIND the modal and is invisible while dragging (PFM-2634). */}
      {createPortal(<DragOverlay zIndex={1400}>{activeLoc ? <OverlayRow item={activeLoc.item} /> : null}</DragOverlay>, document.body)}
    </DndContext>
  );
};

// Backwards-compat for any external import of DragRow (none in-tree, but keep
// the symbol exported so nothing breaks if a consumer surfaces later).
export const DragRow = SortableRow;

export default SortableList;
